import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ProgramService } from '../../../../services/aws/program/program.service';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { IClass } from '../../../../models/IProgram';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ClassDetailDialogComponent } from '../../../../widgets/dialog/class-detail-dialog.component';
import { filter, take, takeUntil, map, finalize } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/widgets/confirm-dialog/confirm-dialog.component';
import { Store } from '@ngxs/store';
import { ProgramActions } from '../store/program.action';
import { IErrorMessage } from 'src/app/widgets/error-message/error-message.interface';
import { API_USER_DEFINED_MESSAGE } from 'src/app/widgets/error-message/error-message.const';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { ReportService } from 'src/app/services/aws/report/report.service';
import { ParticipantService } from 'src/app/services/aws/participant/participant.service';
import { TimeToHHmmPipe } from 'src/app/widgets/pipes/timeToHHmm.pipe';
@Component({
    selector: 'program-classes',
    templateUrl: './classes.component.html',
    styleUrls: ['./classes.component.scss'],
    providers: [TimeToHHmmPipe],
    standalone: false
})
export class ClassesComponent implements OnInit, OnDestroy {

    @Input()
    parentSpinner: boolean;

    mode: string = 'view';
    searchmode: string = 'open';
    public errorMessage: IErrorMessage = {};
    classes: IClass[];
    _classes: IClass[];
    programId: any;
    isDesc: boolean = false;
    column: string = 'startDate';
    direction: number = 1
    isTitleAsc: boolean = false;
    isIdAsc: boolean = false;
    public checkInCode: boolean = false;
    checkedInUsersCount: boolean = false;
    isDateAsc: boolean = false;
    isStatusAsc: boolean = false;
    isTimeAsc: boolean = false;
    isLocationAsc: boolean = false;
    isParticipantSelected: boolean;
    isClassSelected: boolean;
    isDisplay: boolean = true;
    showLoading: boolean = false;
    checkAll: boolean = false;
    isSaved: boolean = false;
    public email: string = '';
    private isAdminRole: boolean;
    private readonly destroy$ = new Subject<void>();
    /** Same reference for add/removeEventListener so Back can close the roster dialog without leaving the app shell. */
    private readonly onClassModalPopStateBound = (_event: PopStateEvent) => this.onClassModalPopState();
    private isUnsavedConfirmOpen = false;
    private unsavedConfirmDialogRef: MatDialogRef<ConfirmDialogComponent> | null = null;
    private suppressPopstateHandling = false;

    constructor(private programService: ProgramService, private route: ActivatedRoute, private dialog: MatDialog, private store: Store,
        private auth: AuthenticationService, private router: Router, private reportService: ReportService, private participantService: ParticipantService, private timeToHHmmPipe: TimeToHHmmPipe,
        private ngZone: NgZone) {
        this.isParticipantSelected = false;
        this.isClassSelected = false;
        this.route.params
            .pipe(take(1))
            .subscribe(params => {
                this.programId = params["id"];
            });
    }

    ngOnInit() {
        this.isAdminRole = (this.router.url.startsWith('/admin/programs-external') ? false : true);
        this.router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                takeUntil(this.destroy$)
            )
            .subscribe((val: NavigationStart) => {
                if (this.programService.isProgramModalOpen || val.navigationTrigger !== 'popstate') {
                    return;
                }
                const segment = this.extractProgramTypeSegment(val.url);
                if (!segment || this.isKnownProgramTabSegment(segment)) {
                    return;
                }
                const id = decodeURIComponent(segment);
                const match = this.classes?.find((c: IClass) => String(c.id) === id || c.id == id);
                if (match) {
                    this.openDialog('classroster', match.id, match.startDate, match?.classSetting?.facilitatorAttendance);
                }
            });
        if (this.isAdminRole) {
            this.getClassesByProgramId(this.programId);
        } else {
            this.auth.getUserEmail().pipe(take(1)).subscribe(e => {
                this.email = e.toString();
                this.getClassesByProgramId(this.programId, this.email);
            });
        }
        window.addEventListener('popstate', this.onClassModalPopStateBound);
    }

    private onClassModalPopState(): void {
        this.ngZone.run(() => {
            if (this.suppressPopstateHandling) {
                this.suppressPopstateHandling = false;
                return;
            }
            if (this.programService.isProgramModalOpen) {
                this.dialog.closeAll();
                this.programService.isProgramModalOpen = false;
                return;
            }
            if (this.shouldBlockTabNavigation()) {
                this.handleClassSettingBrowserBack();
            }
        });
    }

    private handleClassSettingBrowserBack(): void {
        this.programService.handlingClassSettingBrowserBack = true;
        history.pushState({ lhClassSetting: true }, '', window.location.href);
        this.promptExitUnsavedChanges(() => {
            this.discardChanges();
            this.suppressPopstateHandling = true;
            history.back();
        });
    }

    private promptExitUnsavedChanges(onExit: () => void): void {
        this.openUnsavedChangesDialog().pipe(take(1)).subscribe((confirmed) => {
            if (confirmed) {
                onExit();
            }
        });
    }

    getClassesByProgramId(programId, emailId: string = null) {
        this.errorMessage = {};
        this.showLoading = this.parentSpinner;
        const classService = this.isAdminRole ?
            this.programService.getClassesByProgram(programId) :
            this.participantService.getExternalFacilitatorCourses(programId);

        classService
            .subscribe((response: IClass[]) => {
                this.showLoading = false;
                this.errorMessage.text = response?.length ? '' : API_USER_DEFINED_MESSAGE.EMPTY_RESPONSE;
                let sortResponse = this.programService?.programSort(response)?.map(item => ({
                    ...item,
                    checkedInUsersCount: item.checkedInUsersCount != null
                        ? item.checkedInUsersCount.toString() : '0'
                }));
                this.classes = sortResponse;
                this._classes = JSON.parse(JSON.stringify(this.classes));
                this.validateIfCheckinCodeExist();
                this.checkAll = response?.length > 0 && response?.every((c: any) => c.classSetting && c.classSetting.facilitatorAttendance);
            },
                () => {
                    this.showLoading = false;
                    this.classes = [];
                    this._classes = JSON.parse(JSON.stringify(this.classes));
                    this.errorMessage.text = API_USER_DEFINED_MESSAGE.FAILED;
                }
            );
    }

    private validateIfCheckinCodeExist(): void {
        this.classes?.forEach((value: IClass) => {
            if (value) {
                value.checkInCode = value?.checkInCode || 'N/A';
            }
        });
    }

    private getList() {
        this.getClassesByProgramId(this.programId);
    }

    toggleclass() {
        this.mode = this.mode != 'edit' ? 'edit' : 'view';
        this.searchmode = this.searchmode != 'close' ? 'close' : 'open';
    }

    sort(property, name, event) {
        event.stopPropagation();

        if (property === 'startTime') {
            const prevCol = this.column;
            const sameTime = prevCol === '_timeKey';
            this.isTimeAsc = sameTime ? !this.isTimeAsc : true;
            this.column = '_timeKey';
            this.direction = this.isTimeAsc ? 1 : -1;
            this.isDesc = this.isTimeAsc;
            const base = this.classes ?? [];
            this.classes = base.map((row: any) => {
                if (!row) return row;
                const startHM = this.timeToHHmmPipe.transform(row.startTime);
                const endHM   = this.timeToHHmmPipe.transform(row.endTime);
                return { ...row, _timeKey: `${startHM}-${endHM}` };
            });
            return;
        }

        this.isDesc = !this.isDesc;
        this.column = property;
        this.direction = this.isDesc ? 1 : -1;
        switch (property) {
            case 'checkInCode':
                this.checkInCode = !this.checkInCode;
                break;
            case 'title':
                this.isTitleAsc = !this.isTitleAsc;
                break;
            case 'classId':
                this.isIdAsc = !this.isIdAsc;
                break;
            case 'startDate':
                {
                    if (name == 'status') this.isStatusAsc = !this.isStatusAsc;
                    else this.isDateAsc = !this.isDateAsc;
                    break;
                }
            case 'location':
                this.isLocationAsc = !this.isLocationAsc;
                break;
            case 'checkedInUsersCount':
                this.checkedInUsersCount = !this.checkedInUsersCount;
                break;
        }
    }

    checkUncheckAll(event) {
        this.checkAll = event.target.checked;
        this.errorMessage = {};
        this.classes.forEach((classInfo: IClass) => {
            const setting = {
                id: classInfo.classSetting ? classInfo.classSetting.id : classInfo.id,
                eventId: classInfo.id,
                facilitatorAttendance: this.checkAll
            } as IClass;
            classInfo.classSetting = setting;
            this.programService.updateClass(setting);
        });
        this.validateIfCheckinCodeExist();
        this.checkAll = this.classes?.length > 0 && this.classes?.every((c: any) => c.classSetting && c.classSetting.facilitatorAttendance);
        this.isDisplay = false;
    }

    saveClass() {
        this.store.dispatch(new ProgramActions.ClearClasses).subscribe(() => { });
        if (this.programService.isDirty('class')) {
            this.programService.save(this.programId, 'class').pipe(take(1)).subscribe(
                () => {
                    this.getClassesByProgramId(this.programId, this.email);
                    this.popOverMessage();
                });
        }

        this.programService.cancelUpdate('class');
    }


    hasUnsavedChanges(): boolean {
        return this.programService.isDirty('class');
    }

    /** True when Class Setting Mode is open or there are unsaved class edits. */
    shouldBlockTabNavigation(): boolean {
        return !this.isDisplay || this.hasUnsavedChanges();
    }

    onCancelClick(): void {
        if (this.isDisplay) {
            return;
        }
        if (!this.hasUnsavedChanges()) {
            this.discardChanges();
            return;
        }
        this.openUnsavedChangesDialog().pipe(take(1)).subscribe((confirmed) => {
            if (confirmed) {
                this.discardChanges();
            }
        });
    }

    openUnsavedChangesDialog(): Observable<boolean> {
        if (this.isUnsavedConfirmOpen && this.unsavedConfirmDialogRef) {
            return this.unsavedConfirmDialogRef.afterClosed().pipe(map((choice) => !!choice));
        }
        this.isUnsavedConfirmOpen = true;
        this.unsavedConfirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: '',
                body: 'Are you sure you want to exit and lose changes?',
                buttonTextNo: 'Keep Editing',
                buttonTextYes: 'Exit'
            },
            panelClass: 'notification-submit-dialog',
            disableClose: true
        });
        return this.unsavedConfirmDialogRef.afterClosed().pipe(
            map((choice) => !!choice),
            finalize(() => {
                this.isUnsavedConfirmOpen = false;
                this.unsavedConfirmDialogRef = null;
                this.programService.handlingClassSettingBrowserBack = false;
            })
        );
    }

    discardChanges(): void {
        this.isDisplay = true;
        this.programService.cancelUpdate('class');
        this.isParticipantSelected = false;
        this.isClassSelected = false;
        this.classes = JSON.parse(JSON.stringify(this._classes));
        this.validateIfCheckinCodeExist();
        this.checkAll = this.classes?.length > 0 && this.classes?.every((c: any) => c.classSetting && c.classSetting.facilitatorAttendance);
        if (this.mode === 'edit') {
            this.mode = 'view';
            this.searchmode = 'open';
        }
    }

    openDialog(componentname, classId, startDate, facilitatorAttendance) {
        if (this.shouldBlockTabNavigation()) {
            if (!this.isUnsavedConfirmOpen) {
                this.openUnsavedChangesDialog().pipe(take(1)).subscribe((confirmed) => {
                    if (confirmed) {
                        this.discardChanges();
                        this.openClassDetailDialog(componentname, classId, startDate, facilitatorAttendance);
                    }
                });
            }
            return;
        }
        this.openClassDetailDialog(componentname, classId, startDate, facilitatorAttendance);
    }

    private openClassDetailDialog(componentname, classId, startDate, facilitatorAttendance) {
        const dialogRef = this.dialog.open(ClassDetailDialogComponent,
            {
                disableClose:true,
                data: {
                    componentname: componentname,
                    programId: this.programId,
                    classId: classId,
                    startDate: startDate,
                    facilitatorAttendance: facilitatorAttendance
                },
                panelClass: 'class-roster-dialog'
            });
        this.programService.isProgramModalOpen = true;
        this.routeCheck(this.isAdminRole);
        dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
            history.pushState({ lhProgramModal: 'class' }, '', window.location.href);
        });
        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            this.getList();
            setTimeout(() => {
                this.routeCheck(this.isAdminRole);
                this.programService.isProgramModalOpen = false;
            }, 30);
        });
    }

    private routeCheck(isAdminRole: boolean): void {
        const target = isAdminRole
            ? `/admin/programs/${this.programId}/classes`
            : `/admin/programs-external/${this.programId}/classes`;
        if (this.router.url.split('?')[0] !== target) {
            this.router.navigateByUrl(target);
        }
    }

    toggleAttendance(classInfo: any, index: number): void {
        const setAttendance = {
            id: classInfo.classSetting ? classInfo.classSetting.id : null,
            eventId: classInfo.id,
            facilitatorAttendance: classInfo.classSetting ? !classInfo.classSetting.facilitatorAttendance : true
        } as IClass;
        classInfo.classSetting = setAttendance;
        this.programService.updateClass(setAttendance);

        this.errorMessage = {};
        this.classes[index].classSetting = setAttendance;
        this.checkAll = setAttendance.facilitatorAttendance && this.classes.length > 0 && this.classes.every((c: any) => c.classSetting && c.classSetting.facilitatorAttendance);
        this.validateIfCheckinCodeExist();
        this.isDisplay = false;
    }

    public ngOnDestroy(): void {
        window.removeEventListener('popstate', this.onClassModalPopStateBound);
        this.destroy$.next();
        this.destroy$.complete();
        this.programService.clearClassCache();
    }

    private extractProgramTypeSegment(url: string): string | null {
        if (!url || !this.programId) {
            return null;
        }
        const path = decodeURIComponent(url.split('?')[0]);
        const prefixes = [
            `/admin/programs/${this.programId}/`,
            `/admin/programs-external/${this.programId}/`,
        ];
        for (const p of prefixes) {
            const i = path.indexOf(p);
            if (i >= 0) {
                const rest = path.slice(i + p.length);
                return (rest.split('/')[0] || '').trim() || null;
            }
        }
        return null;
    }

    private isKnownProgramTabSegment(segment: string): boolean {
        const s = segment.toLowerCase();
        return (
            s === 'program-overview' ||
            s === 'classes' ||
            s === 'sessions' ||
            s === 'registration-status' ||
            s === 'program-documents'
        );
    }

    private popOverMessage() {
        this.isDisplay = true;
        this.isSaved = true;
        this.toggleclass();
        setTimeout(() => {
            this.isSaved = false,
                this.toggleclass();
        }, 2000);
    }

    public downloadReport(): void {
        this.reportService.getEventReport(this.programId, "class", 'ClassesReport');
    }

}
